import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { Repeat } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { ChartContainer, type ChartConfig } from '@/ui/base/chart';
import { Tabs, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { PROPS_TOOLTIP, RISCA_GRADE, ROTULO_EIXO } from './temaGrafico';
import type { PontoRevisoesAjustes } from './tipos';

const CONFIG: ChartConfig = {
  abertas: { label: 'Abertas', color: 'hsl(var(--warning))' },
  fechadas: { label: 'Fechadas', color: 'hsl(var(--success))' },
};

/** Gráfico evolutivo de revisões/ajustes (abertas × fechadas), com janela 6/12 meses. */
export function EvolucaoRevisoesAjustes({ dados }: { dados: PontoRevisoesAjustes[] }) {
  const [janela, setJanela] = useState<'6' | '12'>('6');

  const serie = useMemo(() => {
    const recorte = dados.slice(-Number(janela));
    return recorte.map((p) => ({
      mes: format(new Date(`${p.mes}T00:00:00`), 'MMM/yy', { locale: ptBR }),
      abertas: p.abertas,
      fechadas: p.fechadas,
    }));
  }, [dados, janela]);

  const semMovimento = serie.every((p) => p.abertas === 0 && p.fechadas === 0);

  return (
    <Card className="overflow-hidden rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="rounded-lg bg-orange/10 p-1.5">
              <Repeat className="h-4 w-4 text-orange" aria-hidden />
            </div>
            <CardTitle className="text-sm font-bold">Evolução de revisões/ajustes</CardTitle>
          </div>
          <Tabs value={janela} onValueChange={(v) => setJanela(v as '6' | '12')}>
            <TabsList className="h-8">
              <TabsTrigger value="6" className="h-7 text-xs">
                6 meses
              </TabsTrigger>
              <TabsTrigger value="12" className="h-7 text-xs">
                12 meses
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {semMovimento ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Sem revisões/ajustes registrados no período.
          </p>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[480px]">
              <ChartContainer config={CONFIG} className="aspect-auto h-72 w-full">
                <LineChart data={serie} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={RISCA_GRADE} />
                  <XAxis dataKey="mes" tick={ROTULO_EIXO} />
                  <YAxis tick={ROTULO_EIXO} allowDecimals={false} />
                  <Tooltip {...PROPS_TOOLTIP} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="abertas"
                    name="Abertas"
                    stroke="var(--color-abertas)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="fechadas"
                    name="Fechadas"
                    stroke="var(--color-fechadas)"
                    strokeWidth={2}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
